import React from 'react';
import {
  Box,
  Chip,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/system';

import {
  UsageBreakdown,
  hasUsageBreakdown,
  toUsageNumber,
} from '../../utils/usageBreakdown';

interface UsageBreakdownPanelProps {
  usage?: UsageBreakdown | null;
  title?: string;
  compact?: boolean;
  showTitle?: boolean;
  sx?: SxProps<Theme>;
}

const formatTokens = (value: number): string => Math.round(value).toLocaleString();

const formatCurrency = (value: number): string => {
  if (value <= 0) return '$0.0000';
  return `$${value >= 1 ? value.toFixed(2) : value.toFixed(4)}`;
};

export const UsageBreakdownPanel: React.FC<UsageBreakdownPanelProps> = ({
  usage,
  title = 'Usage breakdown',
  compact = false,
  showTitle = true,
  sx,
}) => {
  const theme = useTheme();

  if (!hasUsageBreakdown(usage)) {
    return null;
  }

  const inputTokens = toUsageNumber(usage?.input_tokens);
  const outputTokens = toUsageNumber(usage?.output_tokens);
  const cachedReadTokens = toUsageNumber(usage?.cached_tokens) + toUsageNumber(usage?.cache_read_tokens);
  const cacheWriteTokens = toUsageNumber(usage?.cache_creation_tokens) + toUsageNumber(usage?.cache_write_tokens);
  const totalTokens = toUsageNumber(usage?.total_tokens)
    || inputTokens + outputTokens + cachedReadTokens + cacheWriteTokens;
  const promptCacheCost = toUsageNumber(usage?.prompt_cache_cost)
    || toUsageNumber(usage?.cache_read_cost) + toUsageNumber(usage?.cache_write_cost);
  const totalCost = toUsageNumber(usage?.total_cost);
  const uncategorizedCost = toUsageNumber(usage?.uncategorized_cost);

  const rows = [
    { label: 'Input', value: `${formatTokens(inputTokens)} tok`, cost: toUsageNumber(usage?.input_cost), show: inputTokens > 0 },
    { label: 'Output', value: `${formatTokens(outputTokens)} tok`, cost: toUsageNumber(usage?.output_cost), show: outputTokens > 0 },
    { label: 'Cache read', value: `${formatTokens(cachedReadTokens)} tok`, cost: toUsageNumber(usage?.cache_read_cost), show: cachedReadTokens > 0 },
    { label: 'Cache write', value: `${formatTokens(cacheWriteTokens)} tok`, cost: toUsageNumber(usage?.cache_write_cost), show: cacheWriteTokens > 0 },
    { label: 'Other', value: formatCurrency(uncategorizedCost), cost: null, show: uncategorizedCost > 0.000001 },
  ].filter((row) => row.show);

  return (
    <Box
      sx={[
        {
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: compact ? 1 : 1.5,
          bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.08 : 0.05),
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {showTitle && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTokens(totalTokens)} tok
          </Typography>
        </Stack>
      )}

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {rows.map((row) => (
          <Chip
            key={row.label}
            size="small"
            variant="outlined"
            label={
              row.cost == null
                ? `${row.label}: ${row.value}`
                : `${row.label}: ${row.value} / ${formatCurrency(row.cost)}`
            }
            sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
          />
        ))}
      </Stack>

      {(promptCacheCost > 0 || totalCost > 0) && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
          {promptCacheCost > 0 && (
            <Chip
              size="small"
              color="info"
              variant="filled"
              label={`Prompt cache: ${formatCurrency(promptCacheCost)}`}
            />
          )}
          {totalCost > 0 && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`Total: ${formatCurrency(totalCost)}`}
            />
          )}
        </Stack>
      )}
    </Box>
  );
};
